// data-fit LLC · DecisionFlow · ADMIN Stage
// FiscalYearManager.jsx · DDL: R5 · API: v1.11 · Session 93
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: create validates year_end_date > year_start_date; PATCH fields limited; no tenant_id in bodies
// Patches: PATCH-S93-FYM-01 (authHeaders moved inside fetch functions)
// Open Items: OI-ADMIN-FISCAL-YEAR-LIST, OI-ADMIN-FISCAL-YEAR-CREATE, OI-ADMIN-FISCAL-YEAR-PATCH
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './AdminFlowShell'
import { Search } from 'lucide-react'

const INITIAL_FORM_DATA = { fiscal_year_label: '', year_start_date: '', year_end_date: '' }
const INITIAL_EDIT_DATA = { fiscal_year_label: '', year_start_date: '', year_end_date: '' }

const REQUIRED_LABEL_MESSAGE   = 'Fiscal year label is required.'
const REQUIRED_CREATE_MESSAGE  = 'Fiscal year label, start date, and end date are required.'
const INVALID_DATE_MESSAGE     = 'Fiscal year end date must be after start date.'

const getErrorEnvelope = (err) => {
  if (err?.error) { const { code, message } = err.error; return { code, message } }
  return { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred.' }
}

const parseApiResponse = async (response) => {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw payload?.error ? payload : { error: { code: 'HTTP_ERROR', http_status: response.status, message: 'Request failed.' } }
  }
  return payload
}

const getRowsFromEnvelope = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

export default function FiscalYearManager({ aiStatus, aiResponse, currentScreen, onNavigate }) {
  const { token } = useAuth()

  const [fiscalYears, setFiscalYears] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [formError, setFormError] = useState(null)
  const [editError, setEditError] = useState(null)
  const [formData, setFormData] = useState(INITIAL_FORM_DATA)
  const [editRowId, setEditRowId] = useState(null)
  const [editData, setEditData] = useState(INITIAL_EDIT_DATA)
  const [generateModalRowId, setGenerateModalRowId] = useState(null)  // S573
  const [generatePeriodType, setGeneratePeriodType] = useState("monthly")  // S573
  const [generateResult, setGenerateResult] = useState(null)  // S573

  const loadFiscalYears = useCallback(async () => {
    setIsLoading(true); setErrorMessage(null)
    try {
      const response = await fetch('/api/v1/fiscal-years', { method: 'GET', credentials: 'include' })
      const payload = await parseApiResponse(response)
      setFiscalYears(getRowsFromEnvelope(payload))
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setErrorMessage(message || `Unable to load fiscal years. (${code})`)
    } finally {
      setIsLoading(false)
    }
  }, [token])

  useEffect(() => { loadFiscalYears() }, [loadFiscalYears])

  const validateCreateForm = () => {
    if (!formData.fiscal_year_label.trim() || !formData.year_start_date || !formData.year_end_date) { setFormError(REQUIRED_CREATE_MESSAGE); return false }
    if (new Date(formData.year_end_date) <= new Date(formData.year_start_date)) { setFormError(INVALID_DATE_MESSAGE); return false }
    setFormError(null); return true
  }

  const handleCreateSubmit = async (event) => {
    event.preventDefault()
    if (!validateCreateForm()) return
    setIsSubmitting(true); setErrorMessage(null)
    try {
      const body = { fiscal_year_label: formData.fiscal_year_label.trim(), year_start_date: formData.year_start_date, year_end_date: formData.year_end_date }
      const response = await fetch('/api/v1/fiscal-years', { method: 'POST', credentials: 'include', body: JSON.stringify(body) })
      await parseApiResponse(response)
      setFormData(INITIAL_FORM_DATA); setFormError(null)
      await loadFiscalYears()
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setFormError(message || `Unable to create fiscal year. (${code})`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStartEdit  = (row) => { setEditRowId(Number(row.fiscal_year_id)); setEditData({ fiscal_year_label: row.fiscal_year_label, year_start_date: row.year_start_date || '', year_end_date: row.year_end_date || '' }); setEditError(null) }
  const handleCancelEdit = () => { setEditRowId(null); setEditData(INITIAL_EDIT_DATA); setEditError(null) }

  // S573: generate all monthly or quarterly periods for a fiscal year
  const handleGeneratePeriods = async () => {
    if (!generateModalRowId) return
    setIsSubmitting(true); setGenerateResult(null)
    try {
      const response = await fetch(`/api/v1/fiscal-years/${generateModalRowId}/generate-periods`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period_type: generatePeriodType }),
      })
      const payload = await parseApiResponse(response)
      setGenerateResult({ success: true, created: payload.created, skipped: payload.skipped })
      await loadFiscalYears()
    } catch (err) {
      const { message } = getErrorEnvelope(err)
      setGenerateResult({ success: false, message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveEdit = async (row) => {
    if (!editData.fiscal_year_label.trim()) { setEditError(REQUIRED_LABEL_MESSAGE); return }
    setIsSubmitting(true); setEditError(null)
    try {
      const fiscalYearId = Number(row.fiscal_year_id)
      const body = { fiscal_year_label: editData.fiscal_year_label.trim(), year_start_date: editData.year_start_date, year_end_date: editData.year_end_date }
      const response = await fetch(`/api/v1/fiscal-years/${fiscalYearId}`, { method: 'PATCH', credentials: 'include', body: JSON.stringify(body) })
      await parseApiResponse(response)
      setEditRowId(null); setEditData(INITIAL_EDIT_DATA)
      await loadFiscalYears()
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setEditError(message || `Unable to update fiscal year. (${code})`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // S579 — search only (no sort — inline edit rows make sort a footgun)
  const [search, setSearch] = useState('')
  const filteredYears = useMemo(() => {
    const q = search.toLowerCase()
    return fiscalYears.filter((y) =>
      !q || (y.fiscal_year_label ?? '').toLowerCase().includes(q)
    )
  }, [fiscalYears, search])

  const inputCls = 'w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-2 py-1 text-tok-sm'

  const renderRows = () => {
    if (isLoading) return <tr><td colSpan="5" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading fiscal years…</td></tr>
    if (filteredYears.length === 0) return <tr><td colSpan="5" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">{search ? 'No fiscal years match your search.' : 'No fiscal years found.'}</td></tr>
    return filteredYears.map((row) => {
      const rowId = Number(row.fiscal_year_id)
      const isEditing = editRowId === rowId
      return (
        <tr key={rowId} className="border-b border-[rgb(var(--color-border))]">
          <td className="px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]">
            {isEditing ? <input type="text" value={editData.fiscal_year_label} onChange={(e) => setEditData((c) => ({ ...c, fiscal_year_label: e.target.value }))} className={inputCls} /> : row.fiscal_year_label}
          </td>
          <td className="px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]">
            {isEditing ? <input type="date" value={editData.year_start_date} onChange={(e) => setEditData((c) => ({ ...c, year_start_date: e.target.value }))} className={inputCls} /> : row.year_start_date}
          </td>
          <td className="px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]">
            {isEditing ? <input type="date" value={editData.year_end_date} onChange={(e) => setEditData((c) => ({ ...c, year_end_date: e.target.value }))} className={inputCls} /> : row.year_end_date}
          </td>
          <td className="px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]">{row.is_active ? 'Active' : 'Inactive'}</td>
          <td className="px-3 py-2 text-[rgb(var(--color-text))]">
            {isEditing ? (
              <div className="flex gap-2">
                <button type="button" className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50" onClick={() => handleSaveEdit(row)} disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Save'}</button>
                <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50" onClick={handleCancelEdit} disabled={isSubmitting}>Cancel</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50" onClick={() => handleStartEdit(row)} disabled={isSubmitting}>Edit</button>
                <button type="button" className="rounded-lg border border-[rgb(var(--color-primary))]/40 px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))]/10 disabled:opacity-50" onClick={() => { setGenerateModalRowId(rowId); setGeneratePeriodType('monthly'); setGenerateResult(null); }} disabled={isSubmitting}>Generate Periods</button>
              </div>
            )}
          </td>
        </tr>
      )
    })
  }

  const formInputCls = 'rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm'

  return (
    <div className="flex flex-col gap-4">
      <div className="mb-1">
        <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">Fiscal Year Manager</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">Create fiscal years and manage active fiscal year records.</p>
      </div>

      {errorMessage && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{errorMessage}</div>}

      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Create Fiscal Year</h3>
        <form className="flex flex-col gap-3" onSubmit={handleCreateSubmit}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
              Fiscal Year Label
              <input type="text" value={formData.fiscal_year_label} onChange={(e) => setFormData((c) => ({ ...c, fiscal_year_label: e.target.value }))} className={formInputCls} />
            </label>
            <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
              Start Date
              <input type="date" value={formData.year_start_date} onChange={(e) => setFormData((c) => ({ ...c, year_start_date: e.target.value }))} className={formInputCls} />
            </label>
            <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
              End Date
              <input type="date" value={formData.year_end_date} onChange={(e) => setFormData((c) => ({ ...c, year_end_date: e.target.value }))} className={formInputCls} />
            </label>
          </div>
          {formError && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{formError}</div>}
          <div>
            <button type="submit" className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create Fiscal Year'}
            </button>
          </div>
        </form>
      </div>

      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Fiscal Years {fiscalYears.length > 0 && <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">({fiscalYears.length})</span>}</h3>
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input type="text" placeholder="Search fiscal years…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none" />
          </div>
        </div>
        {editError && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{editError}</div>}
        <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
          <table className="w-full text-tok-sm">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
              <tr>
                <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Label</th>
                <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Start Date</th>
                <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">End Date</th>
                <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Active</th>
                <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Actions</th>
              </tr>
            </thead>
            <tbody>{renderRows()}</tbody>
          </table>
        </div>
      </div>
      {/* S573 — Generate Periods modal */}
      {generateModalRowId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 flex flex-col gap-4 shadow-xl">
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Generate Periods</h3>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Generates all periods for this fiscal year with correct date ranges. Existing periods are skipped (idempotent).
            </p>
            <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
              Period Type
              <select
                value={generatePeriodType}
                onChange={(e) => setGeneratePeriodType(e.target.value)}
                className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm"
              >
                <option value="monthly">Monthly (12 periods)</option>
                <option value="quarterly">Quarterly (4 periods)</option>
              </select>
            </label>
            {generateResult && generateResult.success && (
              <div className="rounded-lg bg-[rgb(var(--color-success))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))]">
                {generateResult.created} period{generateResult.created !== 1 ? 's' : ''} created, {generateResult.skipped} skipped.
              </div>
            )}
            {generateResult && !generateResult.success && (
              <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                {generateResult.message}
              </div>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={handleGeneratePeriods} disabled={isSubmitting}
                className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
                {isSubmitting ? 'Generating…' : 'Generate'}
              </button>
              <button type="button" onClick={() => { setGenerateModalRowId(null); setGenerateResult(null); }} disabled={isSubmitting}
                className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
